import {Button, Input} from "antd";
import Captcha, {CaptchaCallableFunctions} from "../captcha/Captcha";
import React, {useEffect, useRef, useState} from "react";
import {CONFIG} from "../../../util/config";
import styling from "./LoginForm.module.scss"
import ResetPasswordDialog from "../reset-password/ResetPasswordDialog";
import TfButton from "../../tf-button/TfButton";


export type LoginState = "NO_LICENSE" | "PASSWORD_RESET_REQUIRED" | "TOC_ACCEPT" | "SUCCESS" | "EXCESSIVE_ATTEMPTS" | "EXPIRED_CREDENTIALS" | "UNSUCCESSFUL" | "ERROR" | "RECAPTCHA_FAILURE"

type LoginInfo = {
    username: string,
    password: string,
    captcha: string,
    captchaAt: number | null
}

const loginApi = (loginInfo: { username: string, password: string, captcha: string, captchaAt: number | null }): Promise<LoginState> => {
    return fetch(CONFIG.restPrefix + '/login', {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginInfo),
        ...(CONFIG.isDev && { credentials: 'include' })
    })
        .then(response => response.json())
        .then((response) => response.status as LoginState)
        .catch(error => {
            console.error(error);
            return "ERROR";
        })
}

const LoginForm = ({onLoginChange}: {onLoginChange: (loginState: LoginState) => void }) => {

    const [loginInfo, setLoginInfo] = useState<LoginInfo>({
        username: "",
        password: "",
        captcha: "",
        captchaAt: null
    });
    const [loginState, setLoginState] = useState<LoginState | null>(null);
    const [isResetPasswordOpen, setResetPasswordOpen] = useState(false);
    const captchaRef = useRef<CaptchaCallableFunctions>(null);


    useEffect(() => {
        if (loginState !== null) {
            onLoginChange(loginState)
        }
    }, [loginState, onLoginChange]);

    const resetRecaptcha = () => {
        captchaRef.current?.reset();
    }

    const login = () => {
        loginApi(loginInfo).then((response) => {
            setLoginState(response)
            if (response !== "SUCCESS") {
                resetRecaptcha()
                setLoginInfo(loginInfo => ({
                    ...loginInfo,
                    password: "",
                    captcha: "",
                    captchaAt: null,
                }));
            }
        })
    }

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        const key = name as keyof LoginInfo;

        setLoginInfo(prev => ({
            ...prev,
            [key]: value
        }));
    }

    const handleCaptchaChange = (token: string | null, tokenAt: number) => {
        setLoginInfo(prev => ({
            ...prev,
            captcha: token || "",
            captchaAt: tokenAt
        }));
    };


    const disableLoginButton = Object.values(loginInfo).some(value => value === "" || value === null)

    return <div className={styling.loginForm}>
        {loginState === "ERROR" && <p className={styling.error}>An unexpected error occurred. Please try again</p>}
        {loginState === "UNSUCCESSFUL" && <p className={styling.error}>Incorrect details, or account has expired</p>}
        {loginState === "EXCESSIVE_ATTEMPTS" && <p className={styling.error}>Excessive login failures, please wait 10 minutes.</p>}
        {loginState === "EXPIRED_CREDENTIALS" && <p className={styling.error}>Reset Password has expired, please reset your password again.</p>}
        {loginState === "RECAPTCHA_FAILURE" && <p className={styling.error}>Recaptcha error. Please try logging in again</p>}
        <Input className={styling.username} id={"email"} onChange={onChange} name={"username"} placeholder={"Username or Email"} value={loginInfo.username}/>
        <Input.Password className={styling.password} onChange={onChange} name={"password"} placeholder={"Password"} value={loginInfo.password}/>
        <div className={styling.captcha}>
            <Captcha
                onChange={handleCaptchaChange}
                ref={captchaRef}
            />
        </div>
        <div className={styling.loginButtonRow}>
            <TfButton disabled={disableLoginButton} onClick={login}>
                LOGIN
            </TfButton>
            <Button style={{ padding: 0, margin: 0}} type="link" onClick={() => setResetPasswordOpen(true)}>
                Forgotten your password?
            </Button>
        </div>
        <div style={{ background: "#f5f5f5", padding: "16px 32px" }}>
            <p className={styling.loginSubTitle}>Having Problems?</p>
            <p>Click 'Forgotten your password?' to receive an email with a reset link. If your username is not a valid email or you need assistance please contact <a className={styling.loginHyperlink} href="mailto:support@healthiq.co.uk">Vantage Support team</a></p>
        </div>
        {isResetPasswordOpen && <ResetPasswordDialog onClose={() => setResetPasswordOpen(false)} />}

    </div>
}

export default LoginForm